import * as React from 'react';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {Nut_Settings_User} from 'nutshell-core/settings-constants';

import {DynamicStatusButton} from 'shells/button/dynamic-status-button/dynamic-status-button';
import {MainContentContainer} from 'shells/layout/main-content-container';
import {LoadingPage} from 'shells/loading-page/loading-page';

import {appHistory} from '../../../history';

import {useGetSessionUser} from '../../core/use-get-session-user';
import {useUpdateUserSetting} from '../../core/use-update-user-setting';
import {PageTitleBar} from '../../layout';
import {useGetMeetIntegration} from '../../scheduler/location/conferencing-modals/graphql/hooks/use-get-meet-integration';
import {useSetGoogleMeetIsEnabled} from '../../scheduler/location/conferencing-modals/graphql/hooks/use-set-google-meet-is-enabled';

import {
    getFlashBannerType,
    getPopoverContent,
    getStatusDescription,
    getStatusText,
    handleEnableGoogleIntegration,
    getServiceUrl,
} from '../helpers';
import {PageContent} from '../page-content';
import {USER_SETTING_HAS_ALLOWED_GOOGLE_MEET_WEBHOOK_PROCESSING} from '../tables/consts';

import {useSubscribeGoogleMeetWebhooks} from './graphql/hooks/use-subscribe-google-meet-webhooks';

type Props = ContextRouter;

export function MeetListPage(props: Props) {
    const [errorMessage, setErrorMesssage] = React.useState<string | null | undefined>(null);
    const {googleMeetIntegration, isLoading, refetch, error, startPolling, stopPolling} =
        useGetMeetIntegration();
    const {user} = useGetSessionUser();
    const updateUserSetting = useUpdateUserSetting();
    const {subscribeGoogleMeetWebhooks} = useSubscribeGoogleMeetWebhooks();

    const googleMeetUserEmail = googleMeetIntegration ? googleMeetIntegration.email : '';
    const isGoogleIntegrationConnected = googleMeetIntegration
        ? googleMeetIntegration.isEnabledMeetSync
        : false;
    const setGoogleMeetIsEnabled = useSetGoogleMeetIsEnabled();

    const [isDialogOpen, setIsDialogOpen] = React.useState<boolean>(false);

    const isGoogleMeetEnabled = Boolean(user && user.hasAllowedGoogleMeetWebhookProcessing);

    const enableGoogleMeetWebhookProcessing = () => {
        if (user) {
            updateUserSetting({
                userId: user.id,
                settingName: Nut_Settings_User.SETTING_HAS_ALLOWED_GOOGLE_MEET_WEBHOOK_PROCESSING,
                value: '1',
                propertyOnUser: USER_SETTING_HAS_ALLOWED_GOOGLE_MEET_WEBHOOK_PROCESSING,
            });
        }
    };

    const disableGoogleMeetWebhookProcessing = () => {
        if (user) {
            updateUserSetting({
                userId: user.id,
                settingName: Nut_Settings_User.SETTING_HAS_ALLOWED_GOOGLE_MEET_WEBHOOK_PROCESSING,
                value: '0',
                propertyOnUser: USER_SETTING_HAS_ALLOWED_GOOGLE_MEET_WEBHOOK_PROCESSING,
            });
        }
    };

    const connectGoogleMeet = (onStart?: () => void, onSuccess?: () => void): void => {
        handleEnableGoogleIntegration({
            setGoogleMeetIsEnabled,
            refetch,
            setError: setErrorMesssage,
            onAuthStart: onStart,
            onAuthSuccess: () => {
                if (onSuccess) {
                    onSuccess();
                }
                enableGoogleMeetWebhookProcessing();
                subscribeGoogleMeetWebhooks({isEnabled: true});
            },
        }).then((hasAuth) => {
            if (!hasAuth) {
                setGoogleMeetIsEnabled({isEnabled: false, meetAuthType: 'MEETINGS'});
            }
        });
    };

    const handleReconfigure = () => {
        setGoogleMeetIsEnabled({isEnabled: false, meetAuthType: 'MEETINGS'}).then(() => {
            appHistory.replace(getServiceUrl('GOOGLE_MEET'));
        });
    };

    return (
        <MainContentContainer whiteBackground={true}>
            <PageTitleBar
                title='Google Meet'
                subheaderPrimaryText={
                    isGoogleIntegrationConnected
                        ? 'View meeting details, transcriptions, and summaries all in one place'
                        : 'Connect Nutshell to your Google Meet account for seamless collaboration'
                }
                buttons={[
                    NutClientConfig.hasGoogleMeetTranscription && isGoogleIntegrationConnected && (
                        <DynamicStatusButton
                            key='zoom'
                            text={getStatusText(isGoogleMeetEnabled, error, 'GOOGLE_MEET')}
                            headerType={getFlashBannerType(isGoogleMeetEnabled, error)}
                            headerContent={getStatusDescription(
                                isGoogleMeetEnabled,
                                error,
                                'GOOGLE_MEET'
                            )}
                            isDialogOpen={isDialogOpen}
                            setIsDialogOpen={setIsDialogOpen}
                            popoverContent={getPopoverContent({
                                isEnabled: isGoogleMeetEnabled,
                                errorMessage: error,
                                userEmail: googleMeetUserEmail,
                                handleReconfigure,
                                setIsDialogOpen,
                                serviceType: 'GOOGLE_MEET',
                            })}
                        />
                    ),
                ]}
            />
            {!googleMeetIntegration && isLoading ? (
                <LoadingPage />
            ) : (
                <PageContent
                    errorMessage={errorMessage}
                    setError={setErrorMesssage}
                    refetch={refetch}
                    isConnected={isGoogleIntegrationConnected}
                    isEnabled={isGoogleMeetEnabled}
                    userEmail={googleMeetUserEmail || ''}
                    isLoadingIntegration={isLoading}
                    enableIntegration={enableGoogleMeetWebhookProcessing}
                    disableIntegration={disableGoogleMeetWebhookProcessing}
                    serviceType='GOOGLE_MEET'
                    user={user}
                    connectIntegration={connectGoogleMeet}
                    startPolling={startPolling}
                    stopPolling={stopPolling}
                    {...props}
                />
            )}
        </MainContentContainer>
    );
}
